<script setup lang="ts">
import { computed } from 'vue-demi'
import { useI18n } from 'vue-i18n'
import { ProjektLokalita, ProjektLokalitaRolaEnum } from '/@src/client-portal'
import { AdresaPublicResponse } from '/@src/client-public'
import { useSteps } from '/@src/composable/useSteps'
import { NazvyKrokov } from '/@src/utils/zoppm/types'
import { inject, Ref } from 'vue'
import { ZoppmSchemeTransformer } from '/@src/utils/zoppm'
import { NazvyAssemblySekcii } from '/@src/utils/zoppm/ZoppmSchemeTransformer/types'

export interface MiestoRealizacie {
  miestaRealizacie: ProjektLokalita[]
  isValidSection: boolean
}

const props = withDefaults(defineProps<MiestoRealizacie>(), {
  miestaRealizacie: undefined,
  isValidSection: false,
})

const stavPodana = inject<boolean>('stavPodana', false)

const { t } = useI18n()
const steps = useSteps()

const miestaRealizacieData = computed(() => props?.miestaRealizacie)

const getAdresa1 = (adresa: AdresaPublicResponse) => {
  const ulica = adresa?.ulica
  const cislo = adresa?.supisneCislo
    ? `${adresa?.supisneCislo}/${adresa?.orientacneCislo}`
    : adresa?.orientacneCislo
  const psc = adresa?.psc
  const obec = adresa?.obec?.nazov ? `${adresa?.obec?.nazov}, ` : ''

  return `${ulica} ${cislo} ${psc} ${obec}`.replaceAll('undefined', '')
}

const getAdresa2 = (adresa: AdresaPublicResponse) => {
  const kraj = adresa?.kraj?.nazov ? `${adresa?.kraj?.nazov}, ` : ''
  const stat = adresa?.stat?.nazov

  return `${kraj}${stat}`.replaceAll('undefined', '')
}

const getKatastralneUzemie = (miesto: ProjektLokalita) => {
  if (!miesto?.katastralneUzemie?.kod && !miesto?.katastralneUzemie?.nazov) {
    return
  }

  const divider =
    miesto?.katastralneUzemie?.kod && miesto?.katastralneUzemie?.nazov ? ' - ' : ''

  return `${miesto?.katastralneUzemie?.kod}${divider}${miesto?.katastralneUzemie?.nazov}`
}

const schemeTransformer = inject<Ref<ZoppmSchemeTransformer>>('schemeTransformer')
const sekciaRefNazov: NazvyAssemblySekcii = 'zoppm.ss.lokality'
const heading = computed(
  () =>
    schemeTransformer?.value.getSectionTitle(sekciaRefNazov) ||
    t('zoppm.zhrnutie.miestaRealizacie.mainHeading')
)
</script>

<template>
  <IDSKHeading as="h2" variant="l" class="mb-7">
    {{ heading }}
  </IDSKHeading>

  <ZhrnutieError v-if="isValidSection" />

  <template v-for="(miesto, key) in miestaRealizacieData" :key="key">
    <div class="mb-5">
      <IDSKHeading as="h3" variant="s" class="mb-2">
        {{ miesto.nazov }}
      </IDSKHeading>
      <IDSKParagraph class="mb-0">
        <span class="d-block">{{ getAdresa1(miesto.adresa) }}</span>
        {{ getAdresa2(miesto.adresa) }}
      </IDSKParagraph>
      <IDSKParagraph v-if="getKatastralneUzemie(miesto)" class="mb-0">
        {{ t('zoppm.zhrnutie.miestaRealizacie.katastralneUzemie') }}:
        {{ getKatastralneUzemie(miesto) }}
      </IDSKParagraph>
      <IDSKParagraph v-if="miesto?.parcelneCislo" class="mb-0">
        {{ t('zoppm.zhrnutie.miestaRealizacie.parcelneCislo') }}:
        {{ miesto?.parcelneCislo }}
      </IDSKParagraph>
      <IDSKParagraph
        v-if="miesto?.rola === ProjektLokalitaRolaEnum['PrimarnaLokalitaRealizacie']"
        class="mb-0"
      >
        <b>{{ t('zoppm.zhrnutie.miestaRealizacie.prevazujuceMiestoRealizcie') }}</b>
      </IDSKParagraph>
    </div>
  </template>

  <div v-if="!stavPodana" class="d-flex align-items-right">
    <IDSKButton
      variant="secondary"
      type="button"
      class="mb-0"
      :data-test-id="`zoppm-miesta-realizacie-edit`"
      @click="
        () =>
          steps.setStep(NazvyKrokov['miestoRealizacie'], {
            ref: NazvyKrokov['zhrnutie'],
          })
      "
    >
      {{ t('zoppm.zhrnutie.editBtn') }}
    </IDSKButton>
  </div>

  <hr class="mb-8 mt-8" />
</template>
